/**
 * Upload weekly Keyfuels diesel price to ZeeCore (declared ingest).
 * PDF bytes (when present) are stored in RawPayloadStore with the structured extract.
 */

import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from './apiClient';
import { DECLARED_INGEST_TIMEOUT_MS } from './fuelTransactionUpload';
import {
  KEYFUELS_DIESEL_PRICE_MAX_PDF_BYTES,
  KEYFUELS_DIESEL_PRICE_MAX_PPL,
} from './fuelPriceLimits';
import { isValidCalendarYmd } from './calendarYmd';

export type FuelPriceEntryMethod = 'pdf_regex' | 'manual';

export type FuelPriceIngestBody = {
  week_commencing: string;
  pence_per_litre: number;
  entry_method: FuelPriceEntryMethod;
  matched_line?: string;
  filename?: string;
  pdfBase64?: string;
  /** Optional; ZeeCore defaults at the edge when omitted. */
  transport_mode?: 'road' | 'rail' | 'maritime' | 'air' | 'intermodal';
};

export type FuelPriceIngestResponse = {
  status?: string;
  job_id?: string;
  correlation_id?: string;
  raw_object_id?: string;
  message?: string;
  error?: string;
};

/** ArrayBuffer → base64 without one huge spread. */
export async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const slice = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...slice);
  }
  return btoa(binary);
}

export function assertKeyfuelsPriceClientValid(body: FuelPriceIngestBody): void {
  if (!isValidCalendarYmd(body.week_commencing)) {
    throw new Error('week_commencing must be a real calendar date (YYYY-MM-DD).');
  }
  if (!Number.isFinite(body.pence_per_litre) || body.pence_per_litre <= 0) {
    throw new Error('pence_per_litre must be a positive number.');
  }
  if (body.pence_per_litre > KEYFUELS_DIESEL_PRICE_MAX_PPL) {
    throw new Error(`pence_per_litre must be ≤ ${KEYFUELS_DIESEL_PRICE_MAX_PPL}.`);
  }
  if (body.entry_method === 'pdf_regex' && !body.pdfBase64) {
    throw new Error('pdfBase64 is required when entry_method is pdf_regex.');
  }
}

export function assertPdfFileSize(file: File): void {
  if (file.size > KEYFUELS_DIESEL_PRICE_MAX_PDF_BYTES) {
    throw new Error(
      `PDF must be ≤ ${Math.round(KEYFUELS_DIESEL_PRICE_MAX_PDF_BYTES / (1024 * 1024))} MB.`,
    );
  }
}

export async function uploadKeyfuelsDieselPrice(
  body: FuelPriceIngestBody,
): Promise<{ response: Response; json: FuelPriceIngestResponse }> {
  assertKeyfuelsPriceClientValid(body);
  const response = await authenticatedFetch(
    API_ENDPOINTS.declaredIngest.keyfuelsDieselPrice(),
    { method: 'POST', body: JSON.stringify(body) },
    DECLARED_INGEST_TIMEOUT_MS,
  );
  const json = (await response.json().catch(() => ({}))) as FuelPriceIngestResponse;
  return { response, json };
}
